import { useMutation, useQuery } from "@apollo/client";
import { Field, Form, Formik, FormikProps } from "formik";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import { UPDATE_EVENT_TEAM_MEMBER_ROLE } from "src/graphql/mutations/event.mutation";
import {
  EVENT_TEAM_ROLES,
  GET_EVENTS_FOR_DASHBOARD,
  GET_TEAM_MEMBER,
} from "src/graphql/queries/event.queries";
import {
  EventOwnerRole,
  EventTeamRolesQuery,
  EventTeamRolesQueryVariables,
  UpdateEventTeamMemberRoleMutation,
  UpdateEventTeamMemberRoleMutationVariables,
} from "src/types/api.d";
import * as Yup from "yup";
import { weddingRoleOptions } from "../_teamRoles";
import styles from "./editTeamMember.module.scss";

const editTeamMemberSchema = Yup.object().shape({
  role: Yup.string().required("Role is required"),
});

interface EditTeamMemberProps {
  teamMemberId: string;
  eventId: number;
  eventUuid: string;
  currentRole: EventOwnerRole;
  teamMemberName: string;
  handleGoBack: () => void;
}

interface Values {
  role: EventOwnerRole;
}

const EditTeamMemberModal: React.FC<EditTeamMemberProps> = ({
  teamMemberId,
  eventId,
  eventUuid,
  currentRole,
  teamMemberName,
  handleGoBack,
}) => {
  const navigate = useNavigate();
  const rolesQuery = useQuery<
    EventTeamRolesQuery,
    EventTeamRolesQueryVariables
  >(EVENT_TEAM_ROLES, { variables: { eventId: eventUuid } });
  const roleOptions = weddingRoleOptions(rolesQuery.data?.ownerTypes || []);

  const [updateTeamMemberRoleFn, { loading: updateRoleLoading }] = useMutation<
    UpdateEventTeamMemberRoleMutation,
    UpdateEventTeamMemberRoleMutationVariables
  >(UPDATE_EVENT_TEAM_MEMBER_ROLE, {
    onCompleted() {
      handleGoBack();
      toast.success(
        <p className="toast">
          {teamMemberName}'s role has been updated successfully.
        </p>
      );
    },
    onError(error) {
      if (error?.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occurred"}</p>
        );
      } else {
        navigate("/clear");
      }
    },
  });

  return (
    <div className={styles["editTeamMember"]}>
      <Formik
        enableReinitialize
        initialValues={{
          role: currentRole,
        }}
        validationSchema={editTeamMemberSchema}
        onSubmit={async (values, actions) => {
          if (
            values.role !== currentRole &&
            roleOptions.some((option) => option.value === values.role)
          ) {
            try {
              await updateTeamMemberRoleFn({
                variables: {
                  teamMemberId: teamMemberId,
                  eventId: eventUuid,
                  role: values.role,
                },
                refetchQueries: [
                  {
                    query: GET_EVENTS_FOR_DASHBOARD,
                    variables: {
                      isInvited: false,
                      page: 1,
                      pageSize: 200,
                    },
                  },
                  {
                    query: GET_TEAM_MEMBER,
                    variables: {
                      eventId: eventId,
                    },
                  },
                ],
              });
            } catch (error) {
              // Error is already handled in the mutation's onError callback
              // Reset Formik's isSubmitting state
              actions.setSubmitting(false);
            }
          } else {
            handleGoBack();
          }
        }}
      >
        {(props: FormikProps<Values>) => (
          <Form className={styles["editTeamMember-form"]}>
            <div className={styles["editTeamMember-form-container"]}>
              <div className={styles["editTeamMember__form-select"]}>
                <label htmlFor="role">Role</label>
                <Field
                  as="select"
                  name="role"
                  className={styles["editTeamMember__select-css"]}
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Field>
              </div>
            </div>
            <div className={styles["editTeamMember-button-container"]}>
              {rolesQuery.error && (
                <p role="alert" className="my-3 text-sm text-red-600">
                  {rolesQuery.error.message}{" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => void rolesQuery.refetch()}
                  >
                    Retry
                  </button>
                </p>
              )}
              <div className={styles["editTeamMember-button"]}>
                <Button
                  type="submit"
                  label="Update Role"
                  loader={updateRoleLoading}
                  disabled={
                    rolesQuery.loading ||
                    !!rolesQuery.error ||
                    props.values.role === currentRole ||
                    !props.isValid ||
                    props.isSubmitting
                  }
                >
                  Update Role
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default EditTeamMemberModal;
